import { WEEKDAY_OPTIONS } from '../constants';
import { CustomRecurrenceFields, Ordinal, OrdinalWeekdayRule, RepeatPreset } from '../types';

const WEEKDAY_VALUES = WEEKDAY_OPTIONS.map((option) => option.value);

/** Maps a JS `Date#getDay()` (0=Sunday) to the AWS weekday code used across this module. */
function weekdayCodeOf(date: Date): string {
  return ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][date.getDay()];
}

const ORDINAL_TO_CRON_NUMBER: Record<Ordinal, string> = {
  first: '1',
  second: '2',
  third: '3',
  fourth: '4',
  fifth: '5',
  last: 'L',
};

const CRON_NUMBER_TO_ORDINAL: Record<string, Ordinal> = {
  '1': 'first',
  '2': 'second',
  '3': 'third',
  '4': 'fourth',
  '5': 'fifth',
};

/**
 * Encodes an {@link OrdinalWeekdayRule} into the cron day-of-week field using the `WEEKDAY#N`/
 * `WEEKDAYL` (nth/last) syntax.
 */
function ordinalRuleToCronDayOfWeek(rule: OrdinalWeekdayRule): string {
  return rule.ordinal === 'last'
    ? `${rule.weekday}L`
    : `${rule.weekday}#${ORDINAL_TO_CRON_NUMBER[rule.ordinal]}`;
}

/** Reverses {@link ordinalRuleToCronDayOfWeek} on a best-effort basis. */
function parseOrdinalDayOfWeek(value: string): OrdinalWeekdayRule | null {
  const lastMatch = /^([A-Z]{3})L$/.exec(value);
  if (lastMatch && WEEKDAY_VALUES.includes(lastMatch[1] as (typeof WEEKDAY_VALUES)[number])) {
    return { ordinal: 'last', weekday: lastMatch[1] as OrdinalWeekdayRule['weekday'] };
  }

  const nthMatch = /^([A-Z]{3})#(\d)$/.exec(value);
  if (
    nthMatch &&
    WEEKDAY_VALUES.includes(nthMatch[1] as (typeof WEEKDAY_VALUES)[number]) &&
    CRON_NUMBER_TO_ORDINAL[nthMatch[2]]
  ) {
    return {
      ordinal: CRON_NUMBER_TO_ORDINAL[nthMatch[2]],
      weekday: nthMatch[1] as OrdinalWeekdayRule['weekday'],
    };
  }

  return null;
}

/**
 * Builds the 6-field AWS EventBridge Scheduler cron expression (`minutes hours day-of-month month
 * day-of-week year`) for a `RepeatPreset`. Non-`custom` presets take their day/weekday/month from
 * `startDate` (the resolved `startsAt`); `custom` uses {@link CustomRecurrenceFields}.
 *
 * There's no `biweekly`/N-week-interval preset: the day-of-week field has no step (`/`) operator,
 * so "every N weeks" can't be encoded in cron at all.
 */
export function buildCronExpression(
  preset: RepeatPreset,
  custom: CustomRecurrenceFields,
  startDate: Date,
): string {
  const minute = startDate.getMinutes();
  const hour = startDate.getHours();
  const dayOfMonth = startDate.getDate();
  const month = startDate.getMonth() + 1;
  const weekday = weekdayCodeOf(startDate);

  switch (preset) {
    case 'every-15-minutes':
      return `${minute}/15 * * * ? *`;
    case 'every-30-minutes':
      return `${minute}/30 * * * ? *`;
    case 'hourly':
      return `${minute} * * * ? *`;
    case 'daily':
      return `${minute} ${hour} * * ? *`;
    case 'weekdays':
      return `${minute} ${hour} ? * MON-FRI *`;
    case 'weekends':
      return `${minute} ${hour} ? * SAT,SUN *`;
    case 'weekly':
      return `${minute} ${hour} ? * ${weekday} *`;
    case 'monthly':
      return `${minute} ${hour} ${dayOfMonth} * ? *`;
    case 'every-3-months':
      return `${minute} ${hour} ${dayOfMonth} ${month}/3 ? *`;
    case 'every-6-months':
      return `${minute} ${hour} ${dayOfMonth} ${month}/6 ? *`;
    case 'yearly':
      return `${minute} ${hour} ${dayOfMonth} ${month} ? *`;
    case 'custom':
      return buildCustomCronExpression(custom, startDate);
    default:
      return `${minute} ${hour} * * ? *`;
  }
}

function buildCustomCronExpression(custom: CustomRecurrenceFields, startDate: Date): string {
  const minute = startDate.getMinutes();
  const hour = startDate.getHours();
  const dayOfMonth = startDate.getDate();
  const month = startDate.getMonth() + 1;
  const year = startDate.getFullYear();
  const interval = Math.max(1, custom.interval || 1);

  if (custom.frequency === 'minutely') return `0/${interval} * * * ? *`;

  if (custom.frequency === 'hourly') return `${minute} 0/${interval} * * ? *`;

  if (custom.frequency === 'daily') return `${minute} ${hour} 1/${interval} * ? *`;

  // no step operator exists for day-of-week, so `interval` doesn't apply here
  if (custom.frequency === 'weekly') {
    const days = custom.weekdays.length > 0 ? custom.weekdays.join(',') : weekdayCodeOf(startDate);
    return `${minute} ${hour} ? * ${days} *`;
  }

  if (custom.frequency === 'monthly') {
    if (custom.monthly.mode === 'ordinal') {
      const dayOfWeek = ordinalRuleToCronDayOfWeek(custom.monthly.ordinal);
      return `${minute} ${hour} ? 1/${interval} ${dayOfWeek} *`;
    }
    const days =
      custom.monthly.daysOfMonth.length > 0
        ? [...custom.monthly.daysOfMonth].sort((a, b) => a - b).join(',')
        : String(dayOfMonth);
    return `${minute} ${hour} ${days} 1/${interval} ? *`;
  }

  // yearly
  const months =
    custom.yearly.months.length > 0
      ? [...custom.yearly.months].sort((a, b) => a - b).join(',')
      : String(month);
  if (custom.yearly.useOrdinalWeekday) {
    const dayOfWeek = ordinalRuleToCronDayOfWeek(custom.yearly.ordinal);
    return `${minute} ${hour} ? ${months} ${dayOfWeek} ${year}/${interval}`;
  }
  return `${minute} ${hour} ${dayOfMonth} ${months} ? ${year}/${interval}`;
}

/** Default `custom` fields, used the first time the "Repetir" select is switched to `custom`. */
export function createDefaultCustomFields(startDate: Date): CustomRecurrenceFields {
  const weekday = weekdayCodeOf(startDate) as OrdinalWeekdayRule['weekday'];
  return {
    frequency: 'weekly',
    interval: 1,
    weekdays: [weekdayCodeOf(startDate)],
    monthly: {
      mode: 'days',
      daysOfMonth: [startDate.getDate()],
      ordinal: { ordinal: 'first', weekday },
    },
    yearly: {
      months: [startDate.getMonth() + 1],
      useOrdinalWeekday: false,
      ordinal: { ordinal: 'first', weekday },
    },
  };
}

export interface ParsedRecurrence {
  preset: RepeatPreset;
  custom: CustomRecurrenceFields;
}

/**
 * Best-effort parser for cron expressions produced by {@link buildCronExpression}. Returns `null`
 * when the expression doesn't match a recognized shape (custom hand-written expressions, `L`/`W`
 * day-of-month wildcards, etc.), in which case callers should fall back to raw expression editing.
 */
export function parseCronExpression(expression: string, startDate: Date): ParsedRecurrence | null {
  const parts = expression.trim().split(/\s+/);
  if (parts.length !== 6) return null;
  const [minute, hour, dayOfMonth, month, dayOfWeek, year] = parts;

  const defaultCustom = createDefaultCustomFields(startDate);
  const asPreset = (preset: RepeatPreset): ParsedRecurrence => ({ preset, custom: defaultCustom });

  const minuteStep = /^\d{1,2}\/(15|30)$/.exec(minute);
  if (
    minuteStep &&
    hour === '*' &&
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek === '?' &&
    year === '*'
  ) {
    return asPreset(minuteStep[1] === '15' ? 'every-15-minutes' : 'every-30-minutes');
  }

  const minutelyStep = /^0\/(\d{1,2})$/.exec(minute);
  if (
    minutelyStep &&
    hour === '*' &&
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek === '?' &&
    year === '*'
  ) {
    return {
      preset: 'custom',
      custom: { ...defaultCustom, frequency: 'minutely', interval: Number(minutelyStep[1]) },
    };
  }

  if (
    /^\d{1,2}$/.test(minute) &&
    hour === '*' &&
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek === '?' &&
    year === '*'
  ) {
    return asPreset('hourly');
  }

  const hourlyStep = /^0\/(\d{1,2})$/.exec(hour);
  if (
    /^\d{1,2}$/.test(minute) &&
    hourlyStep &&
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek === '?' &&
    year === '*'
  ) {
    return {
      preset: 'custom',
      custom: { ...defaultCustom, frequency: 'hourly', interval: Number(hourlyStep[1]) },
    };
  }

  if (/^\d{1,2}$/.test(minute) && /^\d{1,2}$/.test(hour)) {
    if (dayOfMonth === '*' && month === '*' && dayOfWeek === '?' && year === '*') {
      return asPreset('daily');
    }

    if (dayOfMonth === '?' && month === '*' && year === '*') {
      if (dayOfWeek === 'MON-FRI') return asPreset('weekdays');
      if (dayOfWeek === 'SAT,SUN') return asPreset('weekends');
      if (WEEKDAY_VALUES.includes(dayOfWeek as (typeof WEEKDAY_VALUES)[number]))
        return asPreset('weekly');
    }

    if (/^([1-9]|[12]\d|3[01])$/.test(dayOfMonth) && dayOfWeek === '?' && year === '*') {
      if (month === '*') return asPreset('monthly');
      const stepMatch = /^\d{1,2}\/(\d{1,2})$/.exec(month);
      if (stepMatch?.[1] === '3') return asPreset('every-3-months');
      if (stepMatch?.[1] === '6') return asPreset('every-6-months');
      if (/^\d{1,2}$/.test(month)) return asPreset('yearly');
    }

    const dailyStep = /^1\/(\d{1,3})$/.exec(dayOfMonth);
    if (dailyStep && month === '*' && dayOfWeek === '?' && year === '*') {
      return {
        preset: 'custom',
        custom: { ...defaultCustom, frequency: 'daily', interval: Number(dailyStep[1]) },
      };
    }

    if (dayOfMonth === '?' && month === '*' && year === '*') {
      const days = dayOfWeek.split(',');
      if (
        days.length > 1 &&
        days.every((day) => WEEKDAY_VALUES.includes(day as (typeof WEEKDAY_VALUES)[number]))
      ) {
        return {
          preset: 'custom',
          custom: { ...defaultCustom, frequency: 'weekly', weekdays: days },
        };
      }
    }

    const monthStep = /^\d{1,2}\/(\d{1,3})$/.exec(month);
    if (monthStep) {
      if (/^(\d{1,2})(,\d{1,2})*$/.test(dayOfMonth) && dayOfWeek === '?' && year === '*') {
        return {
          preset: 'custom',
          custom: {
            ...defaultCustom,
            frequency: 'monthly',
            interval: Number(monthStep[1]),
            monthly: {
              mode: 'days',
              daysOfMonth: dayOfMonth.split(',').map(Number),
              ordinal: defaultCustom.monthly.ordinal,
            },
          },
        };
      }
      if (dayOfMonth === '?' && year === '*') {
        const ordinal = parseOrdinalDayOfWeek(dayOfWeek);
        if (ordinal) {
          return {
            preset: 'custom',
            custom: {
              ...defaultCustom,
              frequency: 'monthly',
              interval: Number(monthStep[1]),
              monthly: { mode: 'ordinal', daysOfMonth: defaultCustom.monthly.daysOfMonth, ordinal },
            },
          };
        }
      }
    }
  }

  const yearStep = /^(\d{4})\/(\d{1,3})$/.exec(year);
  if (yearStep && /^\d{1,2}$/.test(minute) && /^\d{1,2}$/.test(hour)) {
    if (/^(\d{1,2})(,\d{1,2})*$/.test(month) && /^([1-9]|[12]\d|3[01])$/.test(dayOfMonth)) {
      return {
        preset: 'custom',
        custom: {
          ...defaultCustom,
          frequency: 'yearly',
          interval: Number(yearStep[2]),
          yearly: {
            ...defaultCustom.yearly,
            months: month.split(',').map(Number),
            useOrdinalWeekday: false,
          },
        },
      };
    }
    if (dayOfMonth === '?' && /^(\d{1,2})(,\d{1,2})*$/.test(month)) {
      const ordinal = parseOrdinalDayOfWeek(dayOfWeek);
      if (ordinal) {
        return {
          preset: 'custom',
          custom: {
            ...defaultCustom,
            frequency: 'yearly',
            interval: Number(yearStep[2]),
            yearly: { months: month.split(',').map(Number), useOrdinalWeekday: true, ordinal },
          },
        };
      }
    }
  }

  return null;
}

const NUMERIC_LIST_PATTERN =
  /^(\*|(\d{1,4})(-\d{1,4})?(\/\d{1,4})?(,(\d{1,4})(-\d{1,4})?(\/\d{1,4})?)*)$/;

const MONTH_NAMES = 'JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC';
const WEEKDAY_NAMES = 'SUN|MON|TUE|WED|THU|FRI|SAT';

type CronFieldName = 'minute' | 'hour' | 'dayOfMonth' | 'month' | 'dayOfWeek' | 'year';

const FIELD_PATTERNS: Record<CronFieldName, RegExp> = {
  minute: NUMERIC_LIST_PATTERN,
  hour: NUMERIC_LIST_PATTERN,
  dayOfMonth: /^(\*|\?|\d{1,2}L|L|LW|\d{1,2}W|(\d{1,2})(-\d{1,2})?(,(\d{1,2})(-\d{1,2})?)*)$/,
  month: new RegExp(
    `^(\\*|(\\d{1,2}|${MONTH_NAMES})(-(\\d{1,2}|${MONTH_NAMES}))?(\\/\\d{1,2})?(,(\\d{1,2}|${MONTH_NAMES})(-(\\d{1,2}|${MONTH_NAMES}))?)*)$`,
  ),
  dayOfWeek: new RegExp(
    `^(\\*|\\?|L|(\\d{1}|${WEEKDAY_NAMES})L?(-(\\d{1}|${WEEKDAY_NAMES}))?(#\\d)?(,(\\d{1}|${WEEKDAY_NAMES})(-(\\d{1}|${WEEKDAY_NAMES}))?)*)$`,
  ),
  year: /^(\*|(\d{4})(\/\d{1,3})?(-\d{4})?(,(\d{4})(\/\d{1,3})?(-\d{4})?)*)$/,
};

/**
 * Permissive, best-effort syntax check for a 6-field AWS EventBridge Scheduler cron expression.
 * Validates field count and per-field character sets; it does not fully validate numeric ranges,
 * day-name/number combinations, or every special-character rule from the AWS grammar.
 *
 * @returns an error message, or `null` when the expression looks valid.
 */
export function validateCronExpression(expression: string): string | null {
  const parts = expression.trim().split(/\s+/);
  if (parts.length !== 6) {
    return 'La expresión cron debe tener 6 campos: minutos horas día-del-mes mes día-de-la-semana año.';
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek, year] = parts;
  const fieldsByName: Record<CronFieldName, string> = {
    minute,
    hour,
    dayOfMonth,
    month,
    dayOfWeek,
    year,
  };

  for (const name of Object.keys(fieldsByName) as CronFieldName[]) {
    const value = fieldsByName[name];
    if (!FIELD_PATTERNS[name].test(value)) {
      return `El campo "${name}" tiene un valor inválido: "${value}".`;
    }
  }

  if (dayOfMonth !== '?' && dayOfWeek !== '?') {
    return 'Uno de los campos "día-del-mes" o "día-de-la-semana" debe ser "?".';
  }

  return null;
}
